import React, { useEffect, useState } from 'react';
import { PlusCircle, Trash2, BookOpen, UserCheck, AlertTriangle } from 'lucide-react';

export default function AdminProtocols({ supabase, clients }: { supabase: any, clients: any[] }) {
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  const [clientTasks, setClientTasks] = useState<any[]>([]);
  
  const [selectedClient, setSelectedClient] = useState<string>('');
  
  // New Template form
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [tCategory, setTCategory] = useState('Salon');
  const [tName, setTName] = useState('');
  const [tDesc, setTDesc] = useState('');

  // New Client Task form
  const [showClientTaskForm, setShowClientTaskForm] = useState(false);
  const [ctCategory, setCtCategory] = useState('Salon');
  const [ctName, setCtName] = useState('');
  const [ctDesc, setCtDesc] = useState('');
  const [ctMandatory, setCtMandatory] = useState(false);

  useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      setSelectedClient(clients[0].id);
    }
  }, [clients]);

  useEffect(() => {
    fetchTaskTemplates();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchClientTasks(selectedClient);
    }
  }, [selectedClient]);

  const fetchTaskTemplates = async () => {
    const { data } = await supabase.from('task_templates').select('*').order('category').order('name');
    if (data) setTaskTemplates(data);
  };

  const fetchClientTasks = async (clientId: string) => {
    const { data } = await supabase.from('client_tasks').select('*').eq('client_id', clientId).order('category').order('name');
    if (data) setClientTasks(data);
  };

  const createTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.from('task_templates').insert({ category: tCategory, name: tName, description: tDesc });
    if (!error) {
      setTName(''); setTDesc(''); setShowTemplateForm(false);
      fetchTaskTemplates();
    }
  };

  const deleteTemplate = async (id: string) => {
    if (window.confirm('Supprimer cette tâche du catalogue principal ?')) {
      await supabase.from('task_templates').delete().eq('id', id);
      fetchTaskTemplates();
    }
  };

  const createClientTask = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.from('client_tasks').insert({
      client_id: selectedClient,
      category: ctCategory,
      name: ctName,
      description: ctDesc,
      is_mandatory: ctMandatory
    });
    if (!error) {
      setCtName(''); setCtDesc(''); setShowClientTaskForm(false); setCtMandatory(false);
      fetchClientTasks(selectedClient);
    }
  };

  const deleteClientTask = async (id: string) => {
    if (window.confirm('Supprimer cette tâche pour ce client ?')) {
      await supabase.from('client_tasks').delete().eq('id', id);
      fetchClientTasks(selectedClient);
    }
  };

  const addTemplateToClient = async (t: any) => {
    if (!selectedClient) return;
    await supabase.from('client_tasks').insert({
      client_id: selectedClient,
      category: t.category,
      name: t.name,
      description: t.description,
      is_mandatory: false
    });
    fetchClientTasks(selectedClient);
  };

  const categories = Array.from(new Set(taskTemplates.map(t => t.category)));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white shadow-xl border border-gray-100 rounded-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl mb-1 flex items-center gap-2">
            <BookOpen className="text-gold-500" /> Protocoles & Tâches
          </h2>
          <p className="text-gray-500 text-sm">Gérez le catalogue des tâches et les listes sur-mesure par client.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Catalogue Principal */}
        <div className="bg-white shadow-xl border border-gray-100 rounded-sm p-6 flex flex-col h-[70vh]">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <h3 className="font-serif text-xl flex items-center gap-2"><BookOpen size={20} className="text-gold-500"/> Catalogue Principal</h3>
            <button onClick={() => setShowTemplateForm(!showTemplateForm)} className="text-gold-600 hover:text-black-deep text-sm font-bold uppercase tracking-widest flex items-center gap-1">
              <PlusCircle size={16} /> Ajouter
            </button>
          </div>

          {showTemplateForm && (
            <form onSubmit={createTemplate} className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-sm space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Catégorie (Pièce)</label>
                <input type="text" required value={tCategory} onChange={e => setTCategory(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" placeholder="ex: Cuisine, Salon..." />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Nom de la tâche</label>
                <input type="text" required value={tName} onChange={e => setTName(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" placeholder="ex: Dépoussiérage plinthes" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Description (Optionnel)</label>
                <input type="text" value={tDesc} onChange={e => setTDesc(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowTemplateForm(false)} className="px-3 py-1 text-xs text-gray-500 hover:text-black">Annuler</button>
                <button type="submit" className="px-3 py-1 bg-black text-white text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-gold-500 transition-colors">Créer</button>
              </div>
            </form>
          )}

          <div className="flex-1 overflow-y-auto pr-2 space-y-6">
            {categories.map(cat => (
              <div key={cat as string}>
                <h4 className="text-sm font-bold uppercase tracking-widest text-gray-800 mb-2 border-b border-gray-200 pb-1">{cat as string}</h4>
                <div className="space-y-2">
                  {taskTemplates.filter(t => t.category === cat).map(t => (
                    <div key={t.id} className="flex justify-between items-start p-3 bg-gray-50 border border-gray-100 rounded-sm group hover:border-gold-300 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-black-deep">{t.name}</p>
                        {t.description && <p className="text-xs text-gray-500 mt-1">{t.description}</p>}
                      </div>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => addTemplateToClient(t)} title="Ajouter au client sélectionné" className="text-gold-600 hover:text-black-deep"><PlusCircle size={16}/></button>
                        <button onClick={() => deleteTemplate(t.id)} title="Supprimer du catalogue" className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {taskTemplates.length === 0 && <p className="text-sm text-gray-500 text-center py-8">Aucune tâche dans le catalogue.</p>}
          </div>
        </div>

        {/* Tâches Client */}
        <div className="bg-white shadow-xl border border-gray-100 rounded-sm p-6 flex flex-col h-[70vh]">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <h3 className="font-serif text-xl flex items-center gap-2"><UserCheck size={20} className="text-gold-500"/> Protocole Client</h3>
            <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} className="border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm max-w-[200px] truncate">
              {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </div>

          <div className="mb-4">
            <button onClick={() => setShowClientTaskForm(!showClientTaskForm)} className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-500 hover:text-gold-600 hover:border-gold-500 transition-colors text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 rounded-sm">
              <PlusCircle size={16} /> Ajouter une tâche sur-mesure
            </button>
          </div>

          {showClientTaskForm && (
            <form onSubmit={createClientTask} className="mb-6 p-4 bg-gold-50/30 border border-gold-200 rounded-sm space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Catégorie (Pièce)</label>
                <input type="text" required value={ctCategory} onChange={e => setCtCategory(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" placeholder="ex: Cuisine, Salon..." />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Nom de la tâche</label>
                <input type="text" required value={ctName} onChange={e => setCtName(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" placeholder="ex: Nourrir le chat" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Description (Optionnel)</label>
                <input type="text" value={ctDesc} onChange={e => setCtDesc(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="mandatory" checked={ctMandatory} onChange={e => setCtMandatory(e.target.checked)} className="w-4 h-4 text-gold-500" />
                <label htmlFor="mandatory" className="text-xs font-bold uppercase tracking-widest text-gray-500">Tâche Obligatoire (Requiert photo si non faite)</label>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowClientTaskForm(false)} className="px-3 py-1 text-xs text-gray-500 hover:text-black">Annuler</button>
                <button type="submit" className="px-3 py-1 bg-black text-white text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-gold-500 transition-colors">Créer</button>
              </div>
            </form>
          )}

          <div className="flex-1 overflow-y-auto pr-2 space-y-6">
            {Array.from(new Set(clientTasks.map(t => t.category))).map(cat => (
              <div key={cat as string}>
                <h4 className="text-sm font-bold uppercase tracking-widest text-gray-800 mb-2 border-b border-gray-200 pb-1">{cat as string}</h4>
                <div className="space-y-2">
                  {clientTasks.filter(t => t.category === cat).map(t => (
                    <div key={t.id} className="flex justify-between items-start p-3 bg-white border border-gray-200 shadow-sm rounded-sm group hover:border-gold-300 transition-colors relative overflow-hidden">
                      {t.is_mandatory && <div className="absolute top-0 left-0 w-1 h-full bg-red-500" title="Tâche obligatoire"></div>}
                      <div className="pl-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-black-deep">{t.name}</p>
                          {t.is_mandatory && <span title="Obligatoire"><AlertTriangle size={12} className="text-red-500" /></span>}
                        </div>
                        {t.description && <p className="text-xs text-gray-500 mt-1">{t.description}</p>}
                      </div>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => deleteClientTask(t.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {clientTasks.length === 0 && <p className="text-sm text-gray-500 text-center py-8">Aucune tâche assignée à ce client.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
